'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Action, Loadout } from '@cs-shot/battle-sim';
import { isPartySnapshot } from '@cs-shot/battle-sim';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useTelegramBackButton } from '../../../hooks/useTelegramBackButton';
import translations from './RushMid.translations';
import { useRawInitDataWithDev } from '../../../hooks/useRawInitDataWithDev';
import { useRuntime } from '../../../runtime/RuntimeContext';
import { useNavigation } from '../../../contexts/NavigationContext';
import { extractStartParam } from '../../../utils/telegram';
import { usePartyShare } from '../../../hooks/usePartyShare';
import { useRushMidMatch, type Side, type SidePref } from './useRushMidMatch';
import { useParty } from './useParty';
import { useBattlePreload } from './useBattlePreload';
import { useRushMidSkins } from '../../../queries/useRushMidQueries';
import { useRushMidAudio } from './useRushMidAudio';
import { useSnapshotSync } from './useSnapshotSync';
import { useApiClient } from '../../../contexts/ApiContext';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../lib/queryClient';
import { apiErrorCode, formatApiError } from '../../../utils/apiError';
import { rushMidErrorKey } from './rushMidErrors';
import { JoinMatchmakingRequestSideEnum as SideEnum, JoinMatchmakingRequestMatchModeEnum as ModeEnum } from '../../../services/generated/Api';

/** The tabs shown by the games/fighter/history/rating/raffle TabFilter. */
export type RushMidTab = 'games' | 'fighter' | 'history' | 'rating' | 'raffle';

/** Coerce a legacy or unknown tab param (e.g. the retired 'lobby') to a live tab. */
const normalizeTab = (t?: string): RushMidTab =>
  t === 'fighter' || t === 'history' || t === 'rating' || t === 'raffle' ? t : 'games';

// backend error codes (ValidationException details.errorCode / ApplicationException code) →
// translation key, so the reason is shown in the user's language, not the server's raw string.

/**
 * Composes Rush Mid's client state machine from focused hooks — asset preload
 * ({@link useBattlePreload}), audio ({@link useRushMidAudio}), and snapshot sync + poll
 * ({@link useSnapshotSync}) — and owns the deep-link / auto-party wiring plus every command
 * handler. The screen component consumes this and only routes status → view.
 *
 * Snapshot-driven over HTTP (no Centrifugo): every command returns the authoritative
 * snapshot (applied inline); a poll timer pulls it ~1s while waiting on other players /
 * server timeouts. Phase derives from snapshot.status:
 *   lobby → create/join → OPEN → BUYING → submit → PLAYING… → OVER → result.
 */
export function useRushMidController(initialTab?: string, initialCreate?: boolean) {
  const { t, lang } = useLocalTranslation(translations);
  const mm = useRushMidMatch();
  const party = useParty();
  const navigation = useNavigation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { snapshot, status, apply, setSnapshot, generation } = useSnapshotSync(mm);
  // read by onAction, which the live battle holds as a prop — depending on `snapshot` there
  // would hand it a new function on every ~1s poll
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const { assetsReady: spritesReady, loadProgress, loadError, phraseIdx } = useBattlePreload();
  // fetch the lobby's saved skins/config DURING the loading screen (this hook mounts with the
  // game, i.e. while the loader shows) and hold the loader until it settles — so the lobby paints
  // with its skin/config already in place, no seed-in jerk when it first renders.
  const { isLoading: skinsLoading } = useRushMidSkins();
  const assetsReady = spritesReady && !skinsLoading;
  const partyMembers = snapshot && isPartySnapshot(snapshot) ? snapshot.members.length : null;
  useRushMidAudio(assetsReady, status, partyMembers);

  // Переведённая строка для известного кода, иначе переданный экраном текст. Сообщение
  // сервера сюда не попадает: оно на английском и написано для разработчика.
  const resolveErrorText = useCallback((err: unknown, def: string): string => {
    const key = rushMidErrorKey(err);
    if (key) return t(key);
    console.error('Rush Mid command failed:', formatApiError(err));
    return def;
  }, [t]);

  // lobby/history/rating tab (independent of the live snapshot). Opening a past match
  // pushes a real stack screen (rush-mid-match-detail) so its back + swipe are native —
  // which REMOUNTS this hook on back. The tab is persisted to the screen's navigation
  // params (same pattern as InventoryScreen's filter), so the return restores the tab
  // the user left from (the history list), not a reset lobby.
  const [tab, setTab] = useState<RushMidTab>(normalizeTab(initialTab));
  // OVER initially keeps rendering the battle so the final round animates; the
  // battle's onFinished flips this to show the result screen.
  const [finished, setFinished] = useState(false);
  // Raffle "Играть {ticket}" seeds the create sheet's ticket (saveSoloConfig's cache patch)
  // and switches to the games tab — this flag additionally tells GamesHomeScreen to pop the
  // create sheet open on arrival, mirroring the ?game=/?party= deep-link one-shot pattern
  // (consumed once via consumePendingCreate, not re-armed by a later re-render).
  const [pendingCreate, setPendingCreate] = useState(!!initialCreate);
  const requestCreate = useCallback(() => setPendingCreate(true), []);
  const consumePendingCreate = useCallback(() => setPendingCreate(false), []);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  // the backend code behind the current runtimeError — drives error-screen actions
  // (INSUFFICIENT_RESOURCES adds a top-up button). Only read while runtimeError is set.
  const [runtimeErrorCode, setRuntimeErrorCode] = useState<string | undefined>(undefined);
  const failRuntime = useCallback((err: unknown, def: string) => {
    setRuntimeError(resolveErrorText(err, def));
    setRuntimeErrorCode(apiErrorCode(err));
  }, [resolveErrorText]);
  // "Ещё раз" tapped: hold the result screen (button spins) until the reformed party /
  // solo lobby actually arrives, instead of flashing NONE through the transit.
  const [leavingResult, setLeavingResult] = useState(false);

  // block Telegram back-button + edge swipe-back while seated in an open game / buying / fighting
  useTelegramBackButton({ isDisabled: status === 'OPEN' || status === 'BUYING' || status === 'PLAYING' });

  // deep-link join: `?party=CODE` (web) or Telegram start_param `party_CODE` (TMA) →
  // auto-join the party once on open (the join command guards an already-active user).
  // On TMA `rawInitData` loads async — wait for it before deciding there's no code.
  //
  // "Once" must survive remounts: the start_param lives for the WHOLE TMA session and the
  // ?party= URL stays put, while this ref resets every time the game screen remounts. Without
  // a persistent marker, leaving the game screen and coming back re-joined the party the user
  // had just left. sessionStorage scopes the marker to the webview session — tapping the
  // invite link again relaunches the app with a fresh session and joins as intended.
  const runtime = useRuntime();
  const rawInitData = useRawInitDataWithDev();
  const partyLinkRef = useRef(false);
  useEffect(() => {
    if (partyLinkRef.current) return;
    if (runtime === 'tma' && rawInitData === undefined) return; // initData not ready yet
    partyLinkRef.current = true;
    const fromUrl = new URLSearchParams(window.location.search).get('party');
    const sp = extractStartParam(rawInitData);
    const fromTma = sp && sp.startsWith('party_') ? sp.slice('party_'.length) : null;
    const code = (fromUrl ?? fromTma ?? '').toUpperCase();
    if (code.length < 4) return;
    const consumedKey = `rushmid:party-link:${code}`;
    try {
      if (sessionStorage.getItem(consumedKey)) return;
      sessionStorage.setItem(consumedKey, '1');
    } catch { /* storage unavailable — fall back to per-mount behavior */ }
    // drop ?party= from the web URL so a reload doesn't count as a fresh invite tap
    if (fromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('party');
      window.history.replaceState(null, '', url);
    }
    // A user already searching / seated in a live match must NOT be yanked by an invite tap:
    // pre-check the server state and show THEIR game instead of firing a join the backend
    // rejects — a rejection would replace the live battle with an error screen whose "back"
    // button forfeits the stake. A lingering OVER (the ~120s result retention) is NOT "in a
    // match" — the invite tap means the user is done with that result, so join the party;
    // setSnapshot (not apply) because PARTY ranks below OVER for the monotonic guard.
    mm.getSnapshot()
      .then((s) => {
        if (s.status !== 'NONE' && s.status !== 'PARTY' && s.status !== 'OVER') { apply(s); return; }
        // surface WHY an invite failed (party not found / already in a game / full) instead of
        // silently landing on your own solo lobby.
        return party.joinParty(code).then((p) => setSnapshot(p)).catch((e) => failRuntime(e, 'Не удалось войти в пати'));
      })
      .catch(() => party.joinParty(code).then((p) => setSnapshot(p)).catch((e) => failRuntime(e, 'Не удалось войти в пати')));
  }, [runtime, rawInitData, party, mm, apply, setSnapshot, failRuntime]);

  // Browse an open game as a joiner — pushes its own native stack screen (slot grid, not yet
  // seated) so Telegram back / edge-swipe return here. Does NOT join; the user picks a side from
  // the pushed screen. Routes the games tab underneath to «Игры» so the return lands there.
  const viewGame = useCallback((id: string) => {
    setRuntimeError(null);
    setTab('games');
    navigation.updateCurrentParams({ tab: 'games' });
    navigation.navigate('rush-mid-open-game', { gameId: id });
  }, [navigation]);


  // deep-link OPEN game: `?game=<id>` (web) or Telegram start_param `game_<id>` (TMA) → open
  // that game's slot grid as a joiner (NOT auto-join — the user still picks a side). Mirrors the
  // party plumbing above; the sessionStorage marker keeps a remount from re-opening after the
  // user navigated away, and stripping ?game= from the web URL stops a reload re-triggering it.
  const gameLinkRef = useRef(false);
  useEffect(() => {
    if (gameLinkRef.current) return;
    if (runtime === 'tma' && rawInitData === undefined) return; // initData not ready yet
    gameLinkRef.current = true;
    const fromUrl = new URLSearchParams(window.location.search).get('game');
    const sp = extractStartParam(rawInitData);
    const fromTma = sp && sp.startsWith('game_') ? sp.slice('game_'.length) : null;
    const id = fromUrl ?? fromTma ?? '';
    if (!id) return;
    const consumedKey = `rushmid:game-link:${id}`;
    try {
      if (sessionStorage.getItem(consumedKey)) return;
      sessionStorage.setItem(consumedKey, '1');
    } catch { /* storage unavailable — fall back to per-mount behavior */ }
    if (fromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('game');
      window.history.replaceState(null, '', url);
    }
    viewGame(id);
  }, [runtime, rawInitData, viewGame]);

  // No auto-party: NONE is the solo lobby. A party exists only after "Create party" (leader)
  // or joining by code (member) — so the invite code appears exactly when you opened a party.

  const matchId =
    snapshot && snapshot.status !== 'NONE' && snapshot.status !== 'PARTY'
      ? snapshot.matchId
      : '';

  // Reset `finished` whenever the active match changes — a match must animate its final round
  // before the result. `create` also does this, but a party MEMBER's match starts by poll (they
  // never call it), so without this their 2nd+ match kept a stale finished=true and jumped
  // straight to the result, skipping the last round's replay.
  useEffect(() => { setFinished(false); }, [matchId]);

  // Persist the solo config so the create sheet reopens on the same values (a party's config
  // is already server-backed via the leader's onConfig — this only covers the solo path).
  const saveSoloConfig = useCallback((mode: number, ticket: number, side: Side, matchMode: 'ranked' | 'bots') => {
    const sideEnum = side === 't' ? SideEnum.T : SideEnum.Ct;
    const modeEnum = matchMode === 'bots' ? ModeEnum.Bots : ModeEnum.Ranked;
    apiClient.api.rushMidControllerSaveLobbyConfig({ ticket, mode, side: sideEnum, matchMode: modeEnum }).catch(() => {});
    // the create sheet re-seeds from this query on every open — without the local patch it
    // kept serving the app-boot config for the whole session, so a second game silently
    // reopened (and created) on stale settings
    queryClient.setQueryData(queryKeys.rushMidSkins, (old: unknown) =>
      old && typeof old === 'object' ? { ...old, ticket, mode, side, matchMode } : old);
  }, [apiClient, queryClient]);

  // Create an open game from the sheet: ranked opens a listed OPEN game (participant view);
  // 'bots' returns BUYING straight away (a solo/party practice match). A party leader must first
  // ratify the sheet's config on the party row (the backend is party-row-authoritative and
  // rejects a mismatch), then create. Changing the deal (ticket/mode/side/matchMode) resets
  // every member's ready server-side — so for a 2+ party a CHANGED config stops at the lobby
  // (the members re-confirm, the leader starts again); creating right away would always die
  // on «Не все игроки готовы».
  const create = useCallback((mode: number, ticket: number, side: Side, matchMode: 'ranked' | 'bots') => {
    setRuntimeError(null);
    setFinished(false);
    if (snapshot && snapshot.status === 'PARTY') {
      const p = snapshot;
      const changed = p.mode !== mode || p.ticket !== ticket || p.side !== side || (p.matchMode ?? 'ranked') !== matchMode;
      const needsReconfirm = changed && p.members.length >= 2;
      const flow = needsReconfirm
        ? party.configParty(mode, ticket, side, matchMode)
        : party.configParty(mode, ticket, side, matchMode).then(() => mm.createGame(mode, ticket, side, matchMode));
      flow.then(apply).catch((err) => failRuntime(err, 'Не удалось создать игру'));
      return;
    }
    saveSoloConfig(mode, ticket, side, matchMode);
    mm.createGame(mode, ticket, side, matchMode)
      .then(apply)
      .catch((err) => failRuntime(err, 'Не удалось создать игру'));
  }, [mm, party, snapshot, apply, failRuntime, saveSoloConfig]);

  // Leave the open game we're seated in (participant «Выйти») → back to the solo/party lobby.
  // setSnapshot directly since OPEN→NONE/PARTY is a downgrade the monotonic guard would reject.
  const leaveGame = useCallback(() => {
    setRuntimeError(null);
    mm.leaveGame()
      .then((s) => setSnapshot(s))
      .catch(() => {
        // a rejected leave (the house just filled → BUYING) or a blip — resync to the SERVER's
        // truth instead of a fake NONE that desyncs us from a match we're seated into.
        mm.getSnapshot().then((s) => setSnapshot(s)).catch(() => {});
      });
  }, [mm, setSnapshot]);

  // --- party lobby ---
  const fail = useCallback((def: string) => (err: unknown) => failRuntime(err, def), [failRuntime]);
  const joinPartyByCode = useCallback((code: string) => {
    setRuntimeError(null);
    party.joinParty(code).then(apply).catch(fail('Не удалось войти в пати'));
  }, [party, apply, fail]);
  const partyConfig = useCallback((m: number, tk: number, s: SidePref, mm: 'ranked' | 'bots') => {
    party.configParty(m, tk, s, mm).then(apply).catch(fail('Не удалось изменить настройки пати'));
  }, [party, apply, fail]);
  const partyLeave = useCallback(() => {
    // a rejected leave (party already in a game — the leader started as we tapped) must show
    // the server's truth, not a fake solo lobby that desyncs us from a match we're seated into
    party.leaveParty().then(apply).catch(() => {
      mm.getSnapshot().then((s) => setSnapshot(s)).catch(() => {});
    });
  }, [party, mm, apply, setSnapshot]);
  const partyKick = useCallback((targetUserId: string) => {
    party.kickParty(targetUserId).then(apply).catch(fail('Не удалось исключить игрока'));
  }, [party, apply, fail]);
  const createParty = useCallback(() => {
    setRuntimeError(null);
    party.createParty().then(apply).catch(fail('Не удалось создать пати'));
  }, [party, apply, fail]);
  const partyReady = useCallback((r: boolean) => {
    setRuntimeError(null);
    party.setReady(r).then(apply).catch(fail('Не удалось изменить готовность'));
  }, [party, apply, fail]);
  const partyCode = snapshot?.status === 'PARTY' ? snapshot.code : '';
  const partyShare = usePartyShare(partyCode, t('partyShareText', { code: partyCode }), t('partyJoinAction'));

  const ready = useCallback((loadout: Loadout) => {
    setRuntimeError(null);
    if (!matchId) return;
    // "to battle": submit ready. Best-effort — a transient lock-busy 4xx must not dead-end
    // on an error screen; the buy timeout starts the match with the last SAVED loadout and
    // the next poll takes over.
    mm.submitLoadout(matchId, loadout, true).then(apply).catch((err) => {
      console.warn('[rush-mid] loadout submit failed (non-fatal)', err);
    });
  }, [mm, apply, matchId]);

  // Save the current selection without readying — the buy screen calls this on open and on
  // every change, so the server always holds the player's real loadout (the match never
  // starts with a random kit). Fire-and-forget: a dropped save is harmless (the next change
  // or the ready submit re-sends), and we don't apply the BUYING→BUYING snapshot.
  const saveLoadout = useCallback((loadout: Loadout) => {
    if (!matchId) return;
    mm.submitLoadout(matchId, loadout, false).catch(() => {});
  }, [mm, matchId]);

  const onAction = useCallback(
    (action: Action, round: number) => {
      if (!matchId) return;
      // Don't spend a request on an action the server can only reject: a fighter who died in the
      // round we just watched, a round that has already resolved, or a second submit for the same
      // round. The server keeps its own checks — this just stops the client asking.
      const s = snapshotRef.current;
      if (s?.status === 'PLAYING') {
        if (s.round !== round || s.yourActionSubmitted) return;
        const you = s.fighters.find((f) => f.id === s.yourFighterId);
        if (you && !you.alive) return;
      }
      // action submission is best-effort: a transient/expected 4xx (match briefly
      // locked while resolving, or the round already advanced) must NOT surface as
      // a fatal error that unmounts the live battle — the server auto-acts on
      // timeout and the poll catches the next round. Apply the returned snapshot so
      // a resolved round shows immediately.
      mm.submitAction(matchId, action, round).then(apply).catch((err) => {
        console.warn('[rush-mid] action submit failed (non-fatal)', err);
      });
    },
    [mm, apply, matchId],
  );

  const finish = useCallback(() => setFinished(true), []);
  // Leave the result screen → back to the lobby. A solo player has no party (NONE is the solo
  // lobby); a party player's party is re-formed by the backend the moment the match ends. Either
  // way, don't guess — dismiss the finished match server-side (clears our user→match pointer),
  // then fetch the authoritative snapshot: it comes back as the reformed PARTY lobby (same
  // members) for a party, or NONE (the solo lobby) otherwise. setSnapshot directly (not apply)
  // because OVER→PARTY/NONE is a deliberate downgrade the monotonic guard would reject. Keep
  // `finished` true so the lingering OVER snapshot doesn't re-mount the battle during the fetch.
  //
  // The re-form can lag this fetch by a beat, so a single get would sometimes land on a
  // transient NONE and drop us onto the solo lobby while our party is still alive. Poll a
  // few times until the reformed PARTY/active state surfaces; only fall to NONE (the solo
  // lobby) if the party has genuinely expired.
  const again = useCallback(() => {
    setRuntimeError(null);
    setTab('games');
    // Dismiss (and thereby forfeit-close) a match ONLY from its result screen — this callback
    // also backs the error screen's "back to lobby", and firing leaveMatch at a live match from
    // there would consume the stake + charge the leaver ELO with zero confirmation.
    const id = snapshot?.status === 'OVER' ? matchId : '';
    // Stay ON the result screen while the leaveMatch + party-reform poll resolve — the button
    // shows a spinner (leavingResult), so the tap still feels instant. Dropping to NONE here
    // flashed the solo lobby (and its first-run overlay) for a beat before the reformed party
    // arrived; landing on NONE is now a resolved outcome, not the transit state.
    setLeavingResult(true);
    const gen = generation();
    const dismiss = id ? mm.leaveMatch(id).catch(() => {}) : Promise.resolve();
    dismiss
      .then(async () => {
        for (let i = 0; i < 5; i++) {
          const s = await mm.getSnapshot().catch(() => null);
          if (generation() !== gen) return; // a newer command owns the state — don't overwrite it
          if (s && s.status !== 'OVER' && s.status !== 'NONE') { setSnapshot(s); return; }
          await new Promise((r) => setTimeout(r, 250));
        }
        if (generation() !== gen) return;
        setSnapshot(null); // no surviving party → the solo lobby
      })
      .finally(() => setLeavingResult(false));
  }, [mm, matchId, snapshot, setSnapshot, generation]);
  // error-screen top-up: the balance screen is its own stack screen on both runtimes
  const topUp = useCallback(() => { navigation.navigate('payment'); }, [navigation]);

  const switchTab = useCallback((next: RushMidTab) => {
    setRuntimeError(null);
    setTab(next);
    // persist to the screen's navigation params (InventoryScreen's filter pattern) so a
    // stack push/back (match detail) restores this tab instead of resetting to the lobby
    navigation.updateCurrentParams({ tab: next });
  }, [navigation]);
  // Open a past match as its OWN stack screen so back + edge-swipe are native (return to the
  // history list / result), instead of an in-state view that the stack back would blow past.
  const openMatch = useCallback((id: string, view?: 'result' | 'log') => {
    navigation.navigate('rush-mid-match-detail', { matchId: id, view: view ?? 'result' });
  }, [navigation]);
  // Watch a live match read-only — its own stack screen so back + edge-swipe are native.
  const openSpectate = useCallback((id: string) => {
    navigation.navigate('rush-mid-spectate', { matchId: id });
  }, [navigation]);

  return {
    t, lang,
    snapshot, status, matchId, finished, leavingResult,
    assetsReady, loadProgress, loadError, phraseIdx,
    runtimeError, runtimeErrorCode, topUp,
    tab,
    pendingCreate, requestCreate, consumePendingCreate,
    create, leaveGame, viewGame,
    createParty, joinPartyByCode, partyConfig, partyReady, partyLeave, partyKick, partyShare,
    ready, saveLoadout, onAction, finish, again,
    switchTab, openMatch, openSpectate,
  };
}

export type RushMidController = ReturnType<typeof useRushMidController>;
